import { ScaleBand } from 'd3-scale';

import { DataPoint } from '../../core/types/data-point.js';
import type { MinMax } from '../../core/types/min-max.js';
import { binarySearch } from '../../core/utils/binary-search.js';
import { isBandScale } from '../../core/utils/detect-scale.js';
import { scaleBandInvert } from '../../core/utils/scale.js';
import type { XYChartLinearSelectionContextValue } from '../context/XYChartLinearSelection.context.js';
import { type MapDatapoints } from '../types/xy-chart-internals.js';
import type {
  AxisSelectConfig,
  XYChartSelectionLinearMetadata,
} from '../types/xy-chart-selection.js';
import { XYAccessorDataTypes } from '../types/xy-chart.js';
import { getBandCenteredPoint } from '../utils/get-band-centered-point.js';
import { getCenterDate } from '../utils/get-center-date.js';
import { getCurrentScale } from '../utils/get-scale.js';
import { toAbsolute } from '../utils/value-representation.js';

type SerializeDatapoint = Parameters<
  XYChartLinearSelectionContextValue['searchAllAxisDatapoints']
>[2];

const toComparableNumber = (value: XYAccessorDataTypes): number =>
  value instanceof Date ? value.getTime() : (value as number);

const compareNumericValues = (
  a: XYAccessorDataTypes,
  b: XYAccessorDataTypes,
) => {
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return 0;
};

export const useSortLinearDatapoints = (
  datapointMetadataMap: MapDatapoints,
) => {
  const pending: XYChartSelectionLinearMetadata[] = [];
  const axisSelectConfig = new Map<string, AxisSelectConfig>();
  const sortedValuesByAxis = new Map<string, XYAccessorDataTypes[] | null>();
  let builtCount = 0;

  const handleAddValue = (point: XYChartSelectionLinearMetadata) => {
    pending.push(point);
  };

  const addPointToAxis = (
    axisId: string,
    value: XYAccessorDataTypes,
    point: XYChartSelectionLinearMetadata,
  ) => {
    let config = axisSelectConfig.get(axisId);
    if (config === undefined) {
      config = {
        uniqueXValuesWithDatapoints: new Set<XYAccessorDataTypes>(),
        datapointsPerXAxisValue: new Map<
          XYAccessorDataTypes,
          XYChartSelectionLinearMetadata[]
        >(),
      };
      axisSelectConfig.set(axisId, config);
    }

    let bucket = config.datapointsPerXAxisValue.get(value);
    if (bucket === undefined) {
      bucket = [];
      config.datapointsPerXAxisValue.set(value, bucket);
      config.uniqueXValuesWithDatapoints.add(value);
      sortedValuesByAxis.set(axisId, null);
    }
    bucket.push(point);
  };

  const indexPoint = (point: XYChartSelectionLinearMetadata) => {
    const datapoint = point.datapoint;
    const xValue = datapoint.x0 ?? datapoint.x1;
    if (xValue === undefined) {
      return;
    }

    const axisId = point.series.xAxisId;

    if (typeof xValue === 'string') {
      addPointToAxis(axisId, xValue, point);
      return;
    }

    if (xValue instanceof Date) {
      const x1 = datapoint.x1;
      const value = x1 instanceof Date ? getCenterDate(xValue, x1) : xValue;
      addPointToAxis(axisId, value, point);
      return;
    }

    const { x0, x1 } = datapoint;
    if (typeof x0 === 'number' && typeof x1 === 'number' && x0 !== x1) {
      addPointToAxis(axisId, x0, point);
      addPointToAxis(axisId, x1, point);
      return;
    }

    const metadata = datapointMetadataMap.get(datapoint);
    if (
      metadata?.baseline === 'y' &&
      typeof metadata.accumulator === 'number'
    ) {
      addPointToAxis(axisId, xValue + metadata.accumulator, point);
      return;
    }
    addPointToAxis(axisId, xValue, point);
  };

  const ensureBuilt = () => {
    if (builtCount === pending.length) {
      return;
    }
    for (let i = builtCount; i < pending.length; i++) {
      indexPoint(pending[i]);
    }
    builtCount = pending.length;
  };

  const getSortedValuesByAxisId = (axisId: string) => {
    const cached = sortedValuesByAxis.get(axisId);
    if (cached) {
      return cached;
    }
    const config = axisSelectConfig.get(axisId);
    if (!config) {
      return [];
    }
    const sorted = Array.from(config.uniqueXValuesWithDatapoints).sort(
      compareNumericValues,
    );
    sortedValuesByAxis.set(axisId, sorted);
    return sorted;
  };

  const handleSearchNumericRange = (
    start: number | Date,
    end: number | Date,
    axisId: string,
  ) => {
    const sortedValues = getSortedValuesByAxisId(axisId);
    if (sortedValues.length === 0) {
      return sortedValues;
    }
    const startNum = toComparableNumber(start);
    const endNum = toComparableNumber(end);
    const [minNum, maxNum] =
      startNum <= endNum ? [startNum, endNum] : [endNum, startNum];
    const startIndex = binarySearch(minNum, sortedValues, toComparableNumber);
    let endIndex = binarySearch(maxNum, sortedValues, toComparableNumber);
    if (
      endIndex < sortedValues.length &&
      toComparableNumber(sortedValues[endIndex]) <= maxNum
    ) {
      endIndex++;
    }
    if (
      startIndex >= endIndex ||
      toComparableNumber(sortedValues[startIndex]) < minNum
    ) {
      return [];
    }
    return sortedValues.slice(startIndex, endIndex);
  };

  const handleSearchCategoricalRange = (
    start: number,
    end: number,
    scale: ScaleBand<string>,
  ) => {
    const domainValues = scale.domain();
    const inverter = scaleBandInvert(scale);
    const matched: XYAccessorDataTypes[] = [];
    for (const d of domainValues) {
      const centered = getBandCenteredPoint(scale, d);
      if (centered >= start && centered <= end) {
        matched.push(inverter(centered));
      }
    }
    return matched;
  };

  const handleGetPoints = (
    axisId: string,
    range: XYAccessorDataTypes[],
    callback: SerializeDatapoint,
    pointsByRange: DataPoint[],
  ) => {
    const selectConfig = axisSelectConfig.get(axisId);
    if (!selectConfig || range.length === 0) {
      return pointsByRange;
    }

    const { datapointsPerXAxisValue } = selectConfig;

    for (const value of range) {
      const points = datapointsPerXAxisValue.get(value);
      if (!points) {
        continue;
      }
      for (const point of points) {
        const mapped = callback(point);
        if (!mapped) {
          continue;
        }
        if (Array.isArray(mapped)) {
          for (const item of mapped) {
            pointsByRange.push(item);
          }
        } else {
          pointsByRange.push(mapped);
        }
      }
    }
    return pointsByRange;
  };

  const searchAllAxisDatapoints: XYChartLinearSelectionContextValue['searchAllAxisDatapoints'] =
    (
      start,
      end,
      callback,
      axesIds,
      axisIdToScales,
      axisIdToScaleDomain,
      axisIDToIsRelative,
    ) => {
      ensureBuilt();

      const axesCurrentSelection: Record<string, DataPoint[]> = {};
      const highlightedDatapoints: DataPoint[] = [];
      if (!axisIdToScales) {
        return { axesCurrentSelection, highlightedDatapoints };
      }
      const axesIdsArray = typeof axesIds === 'string' ? [axesIds] : axesIds;
      for (const axisId of axesIdsArray) {
        if (!axisSelectConfig.has(axisId)) {
          continue;
        }
        const scale = getCurrentScale(axisIdToScales, axisId);
        let range: XYAccessorDataTypes[];
        if (isBandScale(scale)) {
          range = handleSearchCategoricalRange(start, end, scale);
        } else {
          let startDomain = scale.invert(start);
          let endDomain = scale.invert(end);
          if (axisIDToIsRelative.get(axisId)) {
            const domain = axisIdToScaleDomain.get(axisId) as MinMax;
            startDomain = toAbsolute(startDomain as number, domain);
            endDomain = toAbsolute(endDomain as number, domain);
          }
          range = handleSearchNumericRange(startDomain, endDomain, axisId);
        }
        axesCurrentSelection[axisId] = handleGetPoints(
          axisId,
          range,
          callback,
          highlightedDatapoints,
        );
      }
      return { axesCurrentSelection, highlightedDatapoints };
    };

  return {
    addValue: handleAddValue,
    getCurrentSelectConfig: () => {
      ensureBuilt();
      return axisSelectConfig;
    },
    searchAllAxisDatapoints,
    hasDatapoints: () => pending.length > 0,
  };
};
